/* eslint-disable react/jsx-fragments */
/* eslint-disable func-names */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import _ from 'lodash';
import useScatterPos from '../chart/UseScatterPos';
import useNodeColor from '../chart/useNodeColor';
import useGetScales from '../chart/useGetScales';
import useChartLegend from '../enhancedFeatures/useChartLegend';
import cacPalette from '../enhancedFeatures/cacPalette';
import * as hf from '../chart/ChartHelpersFuncs';

const CorrScatterChart = props => {
  const chartId = 'InsightsCorrChart';
  const {
    projectId,
    dimensions,
    isCenterChart,
    showPassive,
    isAxisVisible,
    areRowsVisible,
    areColsVisible,
    showLongNames,
    chartView,
    cm,
    rows,
    columns,
    passiveRows,
    passiveCols,
    nodeColors,
    setForceUpdate,
    updateNodes,
    showContScores,
    updateNodeLabelPositions,
    editRowColTitle,
    nodeLabelPositions,
    setProjectSavedFlag,
  } = props;

  const [
    getScales,
    coordValX,
    coordValY,
    getYBoundaryLimit,
    checkYBoundaries,
  ] = useGetScales(chartView);
  const xPadding = 55;
  const yPadding = 60;

  const mergedData = useRef(null);
  const setMergedData = d => {
    mergedData.current = d;
  };

  const svgRef = useRef(null);
  const xAxis = useRef(null);
  const yAxis = useRef(null);
  const hoveredOver = useRef(null);
  const doReposLabels = useRef(false);
  const cScores = useRef({ cMin: 0, cMax: 0 });

  const newChartData = {
    cm: null,
    passiveCols: [],
    passiveRows: [],
    cScoreMin: 0,
    cScoreMax: 0,
  };

  const pCData = useRef({
    cm: null,
    passiveCls: [],
    passiveRows: [],
    cScoreMin: 0,
    cScoreMax: 0,
  });
  const prevChartData = () => pCData.current;
  const setPrevChartData = d => {
    pCData.current = d;
  };
  const [prevProjectId, setPrevProjectId] = useState(-1);
  const [prevChartView, setPrevChartView] = useState(chartView);
  const [prevCenterChart, setPrevCenterChart] = useState(isCenterChart);
  const [prevDimensionY, setPrevDimensionY] = useState(0);
  const [positionLabels] = useScatterPos({ coordValY, coordValX });
  // const [nodeLabelPositions, setnodeLabelPositions] = useState(nodeLabelPositions);

  const [colorToggle, setColorToggle] = useState({
    zeroTo25: true,
    twentySixTo50: true,
    fiftyOneTo75: true,
    seventySixTo100: true,
  });

  const [buildChartLegend, clearChartLegend] = useChartLegend(
    nodeColors.col,
    nodeColors.row,
    colorToggle,
    setColorToggle
  );
  const palette = cacPalette();
  const palettes = {
    cPalette: _.find(palette, { name: nodeColors.col }),
    rPalette: _.find(palette, { name: nodeColors.row }),
    pPalette: _.find(palette, { name: nodeColors.passive }),
  };
  const [getColors, getStrokeAttr, getTextFill] = useNodeColor(
    palettes,
    colorToggle,
    showContScores
  );

  useEffect(() => {
    if (prevProjectId === projectId) return;

    if (mergedData && mergedData.current) setMergedData(() => null);
    if (prevChartData().cm !== null) {
      setPrevChartData(newChartData);
    }
    setPrevProjectId(projectId);
  }, [projectId]);

  // when chartdata changes update local mergedData
  useEffect(() => {
    if (!cm) return;

    const positionsChanged =
      prevChartData().cm !== null &&
      (!_.isEqual(cm.colPoints, prevChartData().cm.colPoints) ||
        !_.isEqual(cm.rowPoints, prevChartData().cm.rowPoints) ||
        !_.isEqual(passiveCols, prevChartData().passiveCols) ||
        !_.isEqual(passiveRows, prevChartData().passiveRows));

    if (
      !prevChartData().cm ||
      positionsChanged ||
      !_.isEqual(chartView, prevChartView) ||
      isCenterChart !== prevCenterChart
    ) {
      const md = hf.mergeData(
        cm,
        passiveRows,
        passiveCols,
        chartView,
        nodeLabelPositions
      );
      doReposLabels.current =
        !md.nodePositionsMatchLength ||
        positionsChanged ||
        ((!nodeLabelPositions || nodeLabelPositions.length === 0) &&
          (isCenterChart !== prevCenterChart ||
            !_.isEqual(chartView, prevChartView)));
      cScores.current.cMin = md.minContribution;
      cScores.current.cMax = md.maxContribution;

      // setMergedData(md.mData.filter(x => x.quadrant === 'topRight'));
      setMergedData(md.mData);

      setPrevChartData({
        cm,
        passiveRows,
        passiveCols,
        cScoreMin: md.minContribution,
        cScoreMax: md.maxContribution,
      });
      setPrevCenterChart(isCenterChart);
      setPrevChartView(chartView);
    }
  }, [cm, passiveRows, passiveCols, isCenterChart, chartView]);

  const xAx = xAxis && xAxis.current;
  const yAx = yAxis && yAxis.current;
  useEffect(() => {
    if (xAxis && xAxis.current) {
      xAxis.current.attr('opacity', isAxisVisible ? 1 : 0);
    }
    if (yAxis && yAxis.current) {
      yAxis.current.attr('opacity', isAxisVisible ? 1 : 0);
    }
  }, [xAx, yAx, isAxisVisible]);

  const showTooltipBox = (
    svg,
    id,
    labelArray,
    yScale,
    xScale,
    maxX,
    node,
    d
  ) => {
    d3.select(node).attr(
      'stroke-width',
      getStrokeAttr(d, cScores.current.cMin, cScores.current.cMax).strokeWidth +
        2
    );

    const lbObj = labelArray.filter(l => l.linkId === d.linkId)[0];
    const txtObj = labelArray.filter(t => t.textId === d.textId)[0];

    const elem = document.getElementById(d.textId);
    elem.innerHTML = txtObj.label;
    elem.style.fontSize = '11px';
    const bbox = elem.getBBox();
    const bboxWidth = (bbox.width + 24) * 1.02;
    const bboxX =
      lbObj.x - bboxWidth + xPadding < 0
        ? -xPadding
        : lbObj.x + bboxWidth - xPadding > xScale(maxX)
        ? xScale(maxX) - (bboxWidth - xPadding)
        : xScale.invert(lbObj.x) > 0
        ? lbObj.x
        : lbObj.x - bboxWidth;
    svg
      .append('rect')
      .attr('class', `${id}_rect`)
      .attr('stroke', 'black')
      .attr('width', (bbox.width + 24) * 1.02) // add 2 percent
      .attr('height', bbox.height + 6)
      .attr('fill', 'white')
      .attr('x', bboxX)
      .attr('y', yScale(txtObj.lblY) - bbox.height);
    svg
      .append('text')
      .attr('class', `${id}_hoverText`)
      .attr('stroke', 'black')
      .style('font-size', '11px')
      .text(txtObj.label)
      .attr('x', bboxX + 4)
      .attr('y', yScale(txtObj.lblY));

    svg.selectAll(`#${d.linkId}`).attr('stroke-width', 2).attr('opacity', 1);

    hoveredOver.current = elem;
    elem.style.opacity = 0;
  };

  const hideTooltipBox = (svg, node, d, labelArray, xScale, maxX) => {
    const txtObj = labelArray.find(t => t.textId === d.textId);


    d3.select(node).attr('stroke-width', node => {
      return getStrokeAttr(node, cScores.current.cMin, cScores.current.cMax)
        .strokeWidth;
    });
    d = { ...d, x: xScale(coordValX(d)) };
    hoveredOver.current.style.opacity = 1;
    hoveredOver.current.style.fontSize = '12px';
    hoveredOver.current.innerHTML = hf.getNodeText(
      d,
      txtObj.label,
      showLongNames,
      xScale,
      xPadding,
      maxX
    );

    svg.selectAll(`#${d.linkId}`).attr('stroke-width', 0.6).attr('opacity', 1);
    svg.selectAll('rect.scatter_rect').remove();
    svg.selectAll('text.scatter_hoverText').remove();
  };

  const nodesGenerator = (
    svg,
    cm,
    xScale,
    yScale,
    id,
    minY,
    maxY,
    minX,
    maxX
  ) => {
    const labelArray = [];

    const links = svg.selectAll(`.${id}_line`);
    const labels = svg.selectAll(`.${id}_lbl`);
    const nodes = svg.selectAll(`.${id}_node`).data(cm, d => d);

    const showNode = node => {
      const rv = _.isUndefined(areRowsVisible) ? true : areRowsVisible;
      const cv = _.isUndefined(areColsVisible) ? true : areColsVisible;

      return !node.isPassive
        ? (node.type === 'col' && cv) || (node.type === 'row' && rv)
          ? 'visible'
          : 'hidden'
        : showPassive
        ? 'visible'
        : 'hidden';
    };

    nodes
      .join(
        enter => enter.append('circle'),
        update => update.attr('class', `${id}_node`),
        exit => exit.remove()
      )
      .attr('class', `${id}_node`)
      .attr('r', node => node.radius)
      .attr('focusable', true)
      .attr('tabindex', '0')
      .attr('fill', node =>
        getColors(node, cScores.current.cMin, cScores.current.cMax)
      )
      .attr(
        'stroke',
        node =>
          getStrokeAttr(node, cScores.current.cMin, cScores.current.cMax).stroke
      )
      .attr(
        'stroke-width',
        node =>
          getStrokeAttr(node, cScores.current.cMin, cScores.current.cMax)
            .strokeWidth
      )
      .attr('id', node => {
        const point = { x: coordValX(node), y: coordValY(node) };
        if (node.label.length > 0) {
          labelArray.push({
            quadrant: hf.quadrant,
            contribution: node.contribution,
            type: node.type,
            isPassive: node.isPassive,
            x: xScale(point.x),
            lblY: node.lblY,
            circleY: yScale(point.y),
            label: node.label,
            origLabel:
              node.type === 'row'
                ? rows[+node.id.split('_')[1]] && rows[+node.id.split('_')[1]].originalTitle
                : columns[+node.id.split('_')[1]] && columns[+node.id.split('_')[1]].originalTitle,
            width: 0.0,
            height: 0.0,
            pointId: node.id,
            textId: node.textId,
            linkId: node.linkId,
            radius: node.radius,
          });
        }
        return node.id;
      })
      .attr('type', node => (node.isPassive ? 'passive' : node.type))
      .attr('opacity', node => (node.isPassive ? 0.8 : 1))
      .attr('visibility', node => showNode(node))
      .attr('cx', node => xScale(coordValX(node)))
      .attr('cy', node => yScale(coordValY(node)))
      .on('mouseover', function (d) {
        showTooltipBox(svg, id, labelArray, yScale, xScale, maxX, this, d);
      })
      .on('mouseout', function (d) {
        hideTooltipBox(svg, this, d, labelArray, xScale, maxX);
      })
      .on('keydown', k => {
        if (d3.event.keyCode === 38) {
          // up key
          k.lblY -= yScale.invert(5);
          svg.selectAll(`#${k.textId}`).attr('y', yScale(k.lblY));
          svg.selectAll(`#${k.linkId}`).attr('y2', yScale(k.lblY));
        } else if (d3.event.keyCode === 40) {
          // down key
          k.lblY += 5;
          svg.selectAll(`#${k.textId}`).attr('y', yScale(k.lblY));
          svg.selectAll(`#${k.linkId}`).attr('y2', yScale(k.lblY));
        }
      });

    hf.buildNodeLabelArray(
      labelArray,
      nodeLabelPositions,
      updateNodeLabelPositions
    );

    links
      .data(labelArray)
      .join(
        enter => enter.append('line').attr('opacity', 1),
        update => update.attr('class', `${id}_line`)
        // exit => exit.remove(),
      )
      .attr('class', `${id}_line`)
      .attr('id', node => {
        const id = node.linkId;
        return id;
      })
      .attr('x1', node => node.x)
      .attr('y1', node => {
        let y = node.circleY;
        y = yScale.invert(y) > 0 ? y + node.radius : y - node.radius;
        return y;
      })
      .attr('x2', node => node.x)
      .attr('y2', node => node.lblY)
      .attr('visibility', node => showNode(node))
      .attr('type', node => (node.isPassive ? 'passive' : node.type))
      .attr('stroke', 'black')
      .attr('stroke-width', 0.8)
      .attr('opacity', 1);

    function dragstarted(label) {
      d3.select(`#${label.pointId}`).raise();
      d3.select(this).attr('cursor', 'grabbing').raise();
    }

    function dragged(label) {
      const y = getYBoundaryLimit(d3.event.y, yScale, minY, maxY, yPadding);
      d3.select(this).attr('y', y);

      label.lblY = yScale.invert(y);

      const link = svg.selectAll(`#${label.linkId}`);
      link
        .attr('opacity', line => {
          const cTop = line.circleY - line.radius;
          const cBot = line.circleY + line.radius;
          return y > cTop && y < cBot ? 0 : 1;
        })
        .attr('y1', line => {
          return yScale(label.lblY) > line.circleY
            ? line.circleY + line.radius
            : line.circleY - line.radius;
        })
        .attr('y2', line => {
          return d3.event.y > line.circleY ? y - 9 : y;
        });
    }

    function dragended(label) {
      d3.select(this).attr('cursor', 'grab');

      // update labelY pos in Reducer ?
      if (mergedData && mergedData.current) {
        const d = mergedData.current.filter(a => a.textId === label.textId)[0];
        d.lblY = label.lblY;
        hf.buildNodeLabelArray(
          labelArray,
          nodeLabelPositions,
          updateNodeLabelPositions
        );
      }
      setProjectSavedFlag(false);
    }

    const handleOnChangeEvent = (editbox, label, d, elem) => {
      const lbl = editbox.value;
      d3.select(label).text(lbl);
      d.label = lbl;
      elem.style.opacity = 1;
    };

    const closeEditBox = (d, txt) => {
      setTimeout(() => {
        d3.select('.fObject')
          .attr('opacity', '0')
          .attr('x', 0)
          .attr('y', -100) // off the screen
          .selectAll('*')
          .remove();

        if (d && txt) {
          const domObject = document.getElementById(d.textId);
          domObject.innerHTML = hf.getNodeText(
            d,
            txt,
            showLongNames,
            xScale,
            xPadding,
            maxX
          );
        }

      }, 500);
    };

    const handleOnBlurEvent = (obj, d) => {
      if (obj) {
        const node = document.getElementById(obj.getAttribute('textid'));

        closeEditBox(
          d, // labelArray.find(x => x.textId === node.textid),
          node.getAttribute('fullLabel')
        );
      }
    };

    const handleKeyPress = (editBox, label, d) => {
      if(d3.event.key === 'Escape') {
        closeEditBox(d.textId, null);
        d3.event.preventDefault();
      } else if (d3.event.key === 'Enter') {
        const tID = d.textId.split('_');
        const newstr = editBox.value;
        editRowColTitle (newstr, tID[0], tID[tID.length - 1]);
        d3.event.preventDefault();
        closeEditBox(d, newstr);
      }
    };

    const handleRestoreTitle = d => {
      const tID = d.textId.split('_');
      editRowColTitle(d.origLabel, tID[0], tID[tID.length - 1]);
      closeEditBox(d, null);
    }
    
    const showEditBox = (label, d, lbObj, yScale, xScale, maxX) => {
      const buttonWidth = 100; //
      const defaultWidth = (lbObj.origLabel !== lbObj.label) ? 620 : 620 - buttonWidth;

      const elem = document.getElementById(d.textId);

      elem.innerHTML = d.label; // get the bounding box for the full label
      const bbox = elem.getBBox();
      elem.innerHTML = hf.getNodeText(d, null, showLongNames, xScale, xPadding, maxX);

      const maxWidth =
        xScale.invert(lbObj.x) > 0
          ? xScale(maxX) + xPadding - lbObj.x
          : lbObj.x + xPadding;
      let bboxWidth = Math.max(defaultWidth, maxWidth); 
      bboxWidth = Math.min(bboxWidth, xScale(maxX) + (2 * xPadding));  // don't overshoot the width of the viewport

      const bboxX =
        xScale.invert(lbObj.x) > 0 // rights or left side
          ? (lbObj.x + bboxWidth > xScale(maxX) + xPadding)  // right side
            ? xScale(maxX) + xPadding - bboxWidth 
            : lbObj.x
          : (lbObj.x - bboxWidth < (0 - xPadding))  // left side
            ? 0-xPadding 
            : lbObj.x - bboxWidth;

      d3.select('.fObject').attr('opacity', 1);

      setTimeout(() => {
        const addDivBlock = fObj => {
          const addEdit = obj => {
            obj
              .append('xhtml:input')
              .attr('type', 'text')
              .attr('autoFocus', 'true')
              .attr('id', 'inputBox')
              .attr('textId', d.textId)
              .attr('value', d.label)
              .on('keydown', function () {
                handleKeyPress(this, label, d);
              })
              .on('change', function () {
                handleOnChangeEvent(this, label, d, elem);
              })
              .on('blur', function () {
                handleOnBlurEvent(this, d);
              })
              .style('font-size', '10px')
              .attr('focusable', true)
              .style('width', '100%')
              .style('height', '24px')
              .style('color', 'black')
              .style('background', 'white')
              .style('border', 'solid 1px black');

            if (lbObj.origLabel !== lbObj.label) {
              obj
                .append('xhtml:button')
                .attr('class', 'scatter-chart-restore-label')
                .attr('aria-label', 'label rollback')
                .attr('type', 'button')
                .text('Restore Original')
                .attr('id', 'editbtn')
                .style('font-family', 'Open Sans')
                .style('font-size', '11px')
                .style('width', `${buttonWidth}px`)
                .style('min-width', `${buttonWidth}px`)
                .style('white-space', 'nowrap')
                .style('height', '100%')
                .style('border', 'solid 2px #DEDEDE')
                .style(
                  'box-shadow',
                  'box-shadow: 5px 3px 20px rgba(0, 0, 0, 0.05);'
                )
                .style('background', 'white')
                .style('border-radius', '0 0 6px 6px')
                .on('mousedown', function () {
                  // onClick happens after blur (that's bad)
                  handleRestoreTitle(d);
                });
            }
          };

          const editb = document.getElementById('editdiv');

          if (!editb) {
            const dObj = fObj
              .append('xhtml:div')
              .attr('id', 'editdiv')
              .style('width', '100%')
              .style('height', '100%')
              .style('border', 'none')
              .style('background', 'transparent')
              .style('display', 'flex');
            addEdit(dObj);
          } else editb.style.opacity = '1';
        };

        const fObj = d3
          .select('.fObject')
          .attr('width', `${bboxWidth}px`) 
          .attr('height', '35px')
          .attr('x', bboxX)
          .attr('y', yScale(d.lblY) - bbox.height);

         addDivBlock(fObj);

        const ibox = d3.select('.inputBox');
        ibox.attr('value', d.label);

        const ibox2 = document.getElementById('inputBox');
        if (ibox2) {
          ibox2.value = d.label;
          ibox2.focus();
          ibox2.select();
        }
      }, 0);
    };

    const dragNode = d3.drag()
      .clickDistance(10)
      .on ('start', (d) => {
        d3.event.sourceEvent.stopPropagation();
        dragstarted(d);
      })
      .on ('drag', dragged)
      .on ('end', dragended);
    
    labels
      .data(labelArray)
      .join(
        enter => enter.append('text'),
        update => update.attr('class', `${id}_lbl`),
        exit => exit.remove()
      )
      .attr('class', `${id}_lbl`)
      .attr('text-anchor', node => {
        const px = xScale.invert(node.x);
        return px > 0 ? 'start' : px < 0 ? 'end' : 'middle';
      })
      .attr('stroke', node =>
        getTextFill(node, cScores.current.cMin, cScores.current.cMax)
      )
      .attr('visibility', node => showNode(node))
      .text(node =>
        hf.getNodeText(node, null, showLongNames, xScale, xPadding, maxX)
      )
      .attr('fullLabel', node => node.label)
      .attr('type', node => (node.isPassive ? 'passive' : node.type))
      .attr('id', node => node.textId)
      .attr('opacity', 1)
      .attr('x', node => node.x)
      .attr('y', node => yScale(node.lblY))
      .attr('cursor', 'grab')
      .call(dragNode)
      .on('dblclick', function (d) {
        if (d3.event.defaultPrevented) return;
        const lbObj = labelArray.filter(l => l.linkId === d.linkId)[0];
        showEditBox(this, d, lbObj, yScale, xScale, maxX);
      })

    links
      .attr('y2', d => {
        const elem = document.getElementById(d.textId);
        if (elem) {
          const bbox = elem.getBBox();
          const y2 =
            d.circleY < yScale(d.lblY)
              ? bbox.y + 2
              : bbox.y + (bbox.height - 2);
          return y2;
        }
        return 0;
      })
      .attr('y1', d => {
        const elem = document.getElementById(d.textId);
        if (elem) {
          const bbox = elem.getBBox();
          const y1 =
            d.circleY < bbox.y ? d.circleY + d.radius : d.circleY - d.radius;
          return y1;
        }
        return 0;
      });
  };

  const buildChart = () => {
    const svg = d3.select(svgRef.current);
    const svgContent = svg.select('.content');

    if (dimensions.width === 0 || dimensions.height === 0) return;

    const filteredData = mergedData.current;
    if (!filteredData || filteredData.length <= 0) return;

    const { xScale, yScale, minY, maxY, minX, maxX } = getScales(
      dimensions,
      filteredData,
      isCenterChart
    );

    if (!doReposLabels.current || prevDimensionY !== dimensions.height) {
      checkYBoundaries(filteredData, yScale, minY, maxY, yPadding);
    }
    if (prevDimensionY !== dimensions.height)
      setPrevDimensionY(dimensions.height);

    if (updateNodes || doReposLabels.current) {
      const hasNodePos = nodeLabelPositions.length !== 0;
      positionLabels(
        filteredData,
        minY,
        maxY,
        yScale,
        xScale,
        hf.getNodeText,
        showLongNames,
        yPadding,
        xPadding,
        maxX
      );
      doReposLabels.current = false;
      setForceUpdate(null);
      if (hasNodePos === true) setProjectSavedFlag(false);
    }

    nodesGenerator(
      svgContent,
      filteredData,
      xScale,
      yScale,
      'scatter',
      minY,
      maxY,
      minX,
      maxX
    );

    // axes
    xAxis.current = svg.select('.x-axis');
    xAxis.current
      .attr('transform', `translate(0, ${yScale(0)})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .attr('opacity', isAxisVisible ? 1 : 0)
      .selectAll('.tick')
      .remove();

    yAxis.current = svg.select('.y-axis');
    yAxis.current
      .attr('transform', `translate(${xScale(0)}, 0)`)
      .call(d3.axisRight(yScale).tickSize(0))
      .attr('opacity', isAxisVisible ? 1 : 0)
      .selectAll('.tick')
      .remove();

    if (showContScores) {
      buildChartLegend(
        svg.select('.legend'),
        xScale(maxX) - 100,
        yScale(maxY) + 30
      );
    } else {
      clearChartLegend(svg.select('.legend'));
    }
  };

  useEffect(() => {
    if (mergedData && mergedData.current) buildChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartView,
    isCenterChart,
    showPassive,
    areRowsVisible,
    areColsVisible,
    dimensions,
    showLongNames,
    updateNodes,
    showContScores,
    colorToggle.zeroTo25,
    colorToggle.twentySixTo50,
    colorToggle.fiftyOneTo75,
    colorToggle.seventySixTo100,
    cScores.current.cMin,
    cScores.current.cMax,
  ]);

  return (
    <React.Fragment>
      <svg
        ref={svgRef}
        id="InsightsCorrChart"
        data-test="InsightsCorrChart"
        className="corrD3_svgBox"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <clipPath id={chartId}>
            <rect x="0" y="0" width="100%" height="100%" />
          </clipPath>
        </defs>
        <g className="x-axis" />
        <g className="y-axis" />
        <g
          className="content"
          clipPath={`url(#${chartId})`}
          data-test="theChart"
        />
        <g className="legend" />
        <foreignObject id="fObject" className="fObject" />
      </svg>
      <span id="ruler" style={{ visibility: 'hidden', whiteSpace: 'nowrap' }} />
    </React.Fragment>
  );
};

CorrScatterChart.propTypes = {
  projectId: PropTypes.number,
  updateNodeLabelPositions: PropTypes.func,
  editRowColTitle: PropTypes.func,
  nodeLabelPositions: PropTypes.arrayOf(PropTypes.shape({})),
  dimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  isCenterChart: PropTypes.bool,
  showPassive: PropTypes.bool,
  isAxisVisible: PropTypes.bool,
  areRowsVisible: PropTypes.bool,
  areColsVisible: PropTypes.bool,
  showLongNames: PropTypes.shape({}),
  chartView: PropTypes.arrayOf(PropTypes.number),
  // chartData: PropTypes.shape({
  //   apparelResp: PropTypes.shape({}),
  //   apparelReq: PropTypes.shape({}),
  cm: PropTypes.shape({
    colPoints: PropTypes.arrayOf(PropTypes.shape({})),
    rowPoints: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  passiveCols: PropTypes.arrayOf(PropTypes.number),
  passiveRows: PropTypes.arrayOf(PropTypes.number),
  // }),
  updateNodes: PropTypes.bool,
  setForceUpdate: PropTypes.func,
  showContScores: PropTypes.bool,
  nodeColors: PropTypes.shape({
    row: PropTypes.string,
    col: PropTypes.string,
    passive: PropTypes.string,
  }),
  setProjectSavedFlag: PropTypes.func,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      originalTitle: PropTypes.string,
    })
  ),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      originalTitle: PropTypes.string,
    })
  ),
};

export default CorrScatterChart;